import React, { useState, useMemo, useCallback } from 'react';
import { View, StyleSheet, ScrollView, ViewStyle } from 'react-native';
import { font as initialFont } from '@styles/fonts';
import { ColorFunction } from '@styles/colors/types';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { isWeb } from '@helpers';
import { SmallLoading } from '@components/loading';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import AccordionRow from './AccordionRow';
import NoData from './NoData';
import ColumnCell from './ColumnCell';
import { useAccordionRow } from '@components/settings/components/common/animatedTable/hooks/useAccordionRow';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { closeComplete } from '@reducers/v2/tableAccordion/tableAccordion.slice';
import { CellRenderer } from '@components/settings/components/common/animatedTable/types';
import { TableContentProps } from './TableContent';
import { AnimatedTableProps } from '@components/settings/components/common/animatedTable';
import { ContainerStyle } from '@interfaces/base';

export interface RenderAccordionContentParams<T = any> {
  accordionData: any;
  C: ColorFunction;
  scale: ScaleFunction;
  font: typeof initialFont;
  lineSize: number;
  containerWidth: number;
  config: any;
  cellRenderer?: CellRenderer<T>;
  valuesAlreadyTranslated: boolean;
  onSubRowPress?: (item: any) => void;
  onSubRowPressIn?: (item: any) => void;
  selectedItem?: T | null;
}

export interface AnimatedRowProps<T = any>
  extends ContainerStyle,
    Required<Pick<AnimatedTableProps, 'config' | 'C' | 'valuesAlreadyTranslated' | 'font' | 'scale' | 'lineSize' | 'showAllButton' | 'array'>>,
    Pick<
      AnimatedTableProps,
      | 'selectedItem'
      | 'getIsSelectedRow'
      | 'getRowKey'
      | 'enableAccordion'
      | 'cellRenderer'
      | 'onSubRowPress'
      | 'onSubRowPressIn'
      | 'renderAccordionContent'
      | 'getAccordionItemsCount' | 'hideBorderForLastItem'
    >,
    Pick<TableContentProps, 'containerWidth'> {
  item: T;
  index: number;
  boxStyle: ViewStyle;
  pressHoverColor: (color: string) => string;
}

const AnimatedRow = <T = any,>({
  item,
  index,
  boxStyle,
  pressHoverColor,
  config,
  C,
  valuesAlreadyTranslated,
  font,
  scale,
  lineSize,
  showAllButton,
  array,
  containerWidth,
  selectedItem,
  getIsSelectedRow,
  getRowKey,
  enableAccordion = false,
  cellRenderer,
  onSubRowPress,
  onSubRowPressIn,
  renderAccordionContent,
  getAccordionItemsCount,
  containerStyle,
  hideBorderForLastItem,
}: AnimatedRowProps<T>) => {
  const dispatch = useAppDispatch();

  const rowKey = enableAccordion && getRowKey ? getRowKey(item, index) : '';
  const accordionState = useAccordionRow(rowKey);
  const isAccordionEnabled = enableAccordion && rowKey !== '';

  const [measuredHeight, setMeasuredHeight] = useState(0);

  // Calculate expected height based on accordion state
  const expectedHeight = useMemo(() => {
    if (!isAccordionEnabled || !accordionState?.isExpanded) return 0;

    // Loading state: always 1 line height
    if (accordionState.isLoading) {
      return scale(lineSize);
    }

    // Data loaded: calculate based on number of items
    if (accordionState.data) {
      // Use provided function to get items count, or fallback to 0
      const itemsCount = getAccordionItemsCount ? getAccordionItemsCount(accordionState.data) : 0;

      // No data: 1 line for NoData component
      if (itemsCount === 0) {
        return scale(lineSize);
      }

      // Data exists: number of items * line height
      return itemsCount * scale(lineSize);
    }

    // Fallback: 1 line height
    return scale(lineSize);
  }, [isAccordionEnabled, accordionState?.isExpanded, accordionState?.isLoading, accordionState?.data, scale, lineSize, getAccordionItemsCount]);

  // Callback for when accordion animation completes
  const handleAccordionAnimationComplete = useCallback(() => {
    if (isAccordionEnabled && accordionState?.isClosing && rowKey !== '') {
      // Animation finished closing, clean up Redux state
      dispatch(closeComplete({ rowKey }));
    }
  }, [isAccordionEnabled, accordionState?.isClosing, rowKey, dispatch]);

  // ---------------- colors & radius ----------------

  // Compute isSelectedRow using provided function or fallback to false
  const isSelectedRow = getIsSelectedRow ? getIsSelectedRow(item, selectedItem) : false;

  // Memoize backgroundColor calculation
  const backgroundColor = useMemo(() => {
    // Only use Redux accordion state
    // When closing, immediately return to normal background (don't wait for animation)
    const isExpanded = isAccordionEnabled && accordionState?.isExpanded && !accordionState?.isClosing;

    if (isSelectedRow) {
      return C('Background/Light/Event Trace');
    } else if (isExpanded) {
      return C('Background/Light/Event Grouped');
    } else {
      return C(pressHoverColor('Background/Light/Field'));
    }
  }, [isAccordionEnabled, accordionState?.isExpanded, accordionState?.isClosing, isSelectedRow, C, pressHoverColor]);

  // Memoize borderRadius calculation
  const borderRadius = useMemo(() => {
    // Only use Redux accordion state
    // When closing, immediately return to normal border radius (don't wait for animation)
    const isExpanded = isAccordionEnabled && accordionState?.isExpanded && !accordionState?.isClosing;

    if (isExpanded) {
      return {
        borderTopLeftRadius: scale(19),
        borderTopRightRadius: scale(19),
      };
    } else if (index === array.length - 1 && !showAllButton && !isExpanded) {
      return {
        borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
        borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
      };
    }
    return {};
  }, [isAccordionEnabled, accordionState?.isExpanded, accordionState?.isClosing, index, array.length, showAllButton, scale]);

  // ---------------- render ----------------
  const renderContent = useMemo(() => {
    const itemConfig = typeof config === 'function' ? config(item) : config;

    return itemConfig.map((column: any, configIndex: number) => (
      <ColumnCell
        key={`config2-${column.type || column.key}-${configIndex}`}
        column={column}
        item={item}
        configIndex={configIndex}
        containerWidth={containerWidth}
        C={C}
        font={font}
        scale={scale}
        cellRenderer={cellRenderer}
        valuesAlreadyTranslated={valuesAlreadyTranslated}
      />
    ));
  }, [config, item, containerWidth, cellRenderer, valuesAlreadyTranslated]);

  // Check if next row has expanded accordion
  const nextRowKey = useMemo(() => {
    if (!enableAccordion || !getRowKey) return '';
    const nextItem = array[index + 1];
    return nextItem ? getRowKey(nextItem, index + 1) : '';
  }, [enableAccordion, getRowKey, array, index]);

  // Always call hook unconditionally (Rules of Hooks)
  // Use empty string as sentinel when there's no next row
  const nextRowAccordionState = useAccordionRow(nextRowKey);
  const isNextRowExpanded = nextRowKey !== '' && nextRowAccordionState?.isExpanded && !nextRowAccordionState?.isClosing;

  // Memoize neighboring item lookups for border logic
  const isNextItemSelected = useMemo(() => {
    const next = array[index + 1];
    return getIsSelectedRow && next ? getIsSelectedRow(next, selectedItem) : false;
  }, [array, index, selectedItem, getIsSelectedRow]);

  // Check if current row has expanded accordion (should hide bottom border)
  const hasExpandedAccordion = isAccordionEnabled && accordionState?.isExpanded && !accordionState?.isClosing;

  const shouldHideBorderTop = !isSelectedRow || index === 0;

  return (
    <View
      style={[
        {
          borderBottomColor: hideBorderForLastItem || hasExpandedAccordion || isNextRowExpanded ? 'transparent' : C('Hairline/Light/No trans'),
          borderBottomWidth: isNextItemSelected || hasExpandedAccordion || isNextRowExpanded ? 0 : StyleSheet.hairlineWidth,
          borderTopColor: shouldHideBorderTop ? 'transparent' : C('Hairline/Light/No trans'),
          borderTopWidth: shouldHideBorderTop ? 0 : StyleSheet.hairlineWidth,
          backgroundColor,
          zIndex: array.length - index,
          ...borderRadius,
        },
        boxStyle,
        containerStyle,
      ]}
    >
      <View style={{ minHeight: scale(lineSize) }}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={[
            {
              paddingRight: scale(30),
              paddingLeft: scale(15),
              flexDirection: 'row',
              alignItems: 'center',
            },
            isWeb && { width: '100%' },
          ]}
        >
          {renderContent}
        </ScrollView>
      </View>

      {/* Render accordion content if expanded */}
      {isAccordionEnabled && accordionState.isExpanded && (
        <AccordionRow
          isExpanded={accordionState.isExpanded && !accordionState.isClosing}
          isClosing={accordionState.isClosing}
          isLoading={accordionState.isLoading}
          expectedHeight={expectedHeight}
          contentHeight={measuredHeight}
          onAnimationComplete={handleAccordionAnimationComplete}
          style={{
            backgroundColor: C('Background/Light/Event Trace'),
            borderBottomLeftRadius: scale(19),
            borderBottomRightRadius: scale(19),
          }}
        >
          <View
            onLayout={(e) => {
              const newHeight = e.nativeEvent.layout.height;
              setMeasuredHeight(newHeight);
            }}
          >
            {accordionState.isLoading ? (
              <View style={{ height: scale(lineSize), alignItems: 'center', justifyContent: 'center' }}>
                <SmallLoading />
              </View>
            ) : renderAccordionContent ? (
              renderAccordionContent({
                accordionData: accordionState.data,
                C,
                scale,
                font,
                lineSize,
                containerWidth,
                config,
                cellRenderer,
                valuesAlreadyTranslated,
                onSubRowPress,
                onSubRowPressIn,
                selectedItem,
              })
            ) : (
              <View style={{ minHeight: scale(lineSize) }}>
                <NoData C={C} loaded font={font} scale={scale} lineSize={lineSize} />
              </View>
            )}
          </View>
        </AccordionRow>
      )}
    </View>
  );
};

export default AnimatedRow;
